import { useTranslation } from 'react-i18next';
import { Button } from 'components/atoms';
import { MagellanPopups } from 'components/molecules';
import Buttons from 'components/molecules/buttons';
import { Form } from 'components/organisms';
import { channelInitialValues } from 'components/organisms/forms/channel/ChannelForm';
import { getTableId } from 'components/organisms/forms/channel/tabs/ChannelVideo';
import { useHasPermission } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import { useEffect } from 'react';
import { PiNavigationArrow } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import {
  useDeleteChannel,
  useGetChannel,
  useGetChannelAllVideosInfinite,
} from 'services/channel';
import { useSyncChannel } from 'services/channel/sync';
import { IChannelSyncAll } from 'services/channel/types/query';
import { useSearchById } from 'store/state';
import { onErrorShowToast } from 'utils/service/errorHandler';

export enum ChannelTabs {
  contract,
  video,
}

const Channel = () => {
  const { t } = useTranslation();
  const channelId = useParamId('channel');
  const navigate = useNavigate();
  const hasPermission = useHasPermission();

  const getChannel = useGetChannel(channelId);
  const deleteChannel = useDeleteChannel(channelId);
  const syncChannel = useSyncChannel();
  const tableId = getTableId(channelId);
  const searchById = useSearchById();

  const filters = searchById[tableId] || {
    videoTitle: '',
    assetId: '',
    externalVideoId: '',
    videoType: undefined,
  };

  const getVideos = useGetChannelAllVideosInfinite(
    { ...filters, channelId },
    false
  );

  const onSyncChannel = async (
    method: keyof IChannelSyncAll,
    startCounter?: () => void
  ) => {
    await syncChannel.mutateAsync(
      {
        channelId,
        updateChannel: method === 'updateChannel' || method === 'updateAll',
        updateVideos: method === 'updateVideos' || method === 'updateAll',
        addNewVideos: method === 'addNewVideos' || method === 'updateAll',
      },
      {
        onSuccess: async () => {
          await getChannel.refetch();
          await getVideos.refetch();
          startCounter && startCounter();
        },
      }
    );
  };

  const onRemoveChannel = async () => {
    await deleteChannel.mutateAsync(undefined, {
      onError: error => onErrorShowToast(error),
      onSuccess: () => navigate('/channel'),
    });
  };

  const navigateToYtChannel = (channelId: string) => {
    window.open(`https://www.youtube.com/channel/${channelId}`, '_blank');
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (syncChannel.isPending) {
      intervalId = setInterval(async () => await getVideos.refetch(), 5000);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [syncChannel.isPending, getVideos.refetch]);

  return (
    <Form.Channel
      status={{ isLoading: getChannel.isLoading, displayMode: true }}
      i18nextPrefix='channel'
      initialValues={
        getChannel.data
          ? {
              ...channelInitialValues,
              ...getChannel.data,
              settlementInfo:
                getChannel.data.settlementInfo ??
                channelInitialValues.settlementInfo,
            }
          : channelInitialValues
      }
      onSubmit={() => {}}
      tittle={getChannel.data?.name ?? '-'}
      topbarContent={
        <div className='flex items-center gap-2'>
          <div className='flex'>
            {getChannel.data ? (
              <Button
                type='button'
                isClear
                onlyIcon
                onClick={() =>
                  navigateToYtChannel(getChannel.data.externalChannelId)
                }>
                <Button.Description description={t('button.open')} />

                <Button.Icon
                  icon={<PiNavigationArrow transform='scale(-1,1)' />}
                />
              </Button>
            ) : null}

            {hasPermission('ChannelPermissionsLIST**', 4) && (
              <MagellanPopups.DeleteWarning
                name={getChannel.data?.name}
                onRemove={onRemoveChannel}
              />
            )}
          </div>
          <Buttons.ChannelSync
            lastSyncDate={getChannel.data?.lastSyncDate}
            onSync={onSyncChannel}
            isPending={syncChannel.isPending}
          />
        </div>
      }
    />
  );
};

export default Channel;
